import {
    FormSelectProps,
    FormSelect as IcebergFormSelect,
    SelectOption
} from '@auviknetworks/iceberg-ui';
import { CircularProgress } from '@mui/material';
import { useFormFactoryContext } from 'application/Shared/Contexts/FormFactoryContext';
import { debug } from 'application/Shared/Utils';
import {
    RunRecipeParams,
    useRunCustomerRecipes
} from 'hooks/dataAccess/Workato/useRunCustomerRecipe';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { ApiOptions } from './Field.type';

export type FormSelectHandlerProps = FormSelectProps & {
    operation?: ApiOptions['operation'];
};

const LoadingIndicator = () => (
    <CircularProgress size="16px" sx={{ marginRight: '8px' }} />
);

const FormSelectWorkatoDataLoader = (props: FormSelectHandlerProps) => {
    const [options, setOptions] = useState<SelectOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const { operation } = props;
    const { action, valueAccessor, label } = operation!;
    const searchOperation = (operation?.search ||
        {}) as ApiOptions['operation']['search'];

    const isApiSearchEnabled = searchOperation?.enabled || false;
    const searchField = searchOperation?.searchField || '';

    // Create a debounced version of setSearch
    const debouncedSetSearch = useMemo(
        () =>
            _.debounce((value: string) => {
                setSearch(value);
            }, 300),
        []
    );

    // Cleanup the debounced function on unmount
    useEffect(
        () => () => {
            debouncedSetSearch.cancel();
        },
        [debouncedSetSearch]
    );

    const { applicationId, clientId } = useFormFactoryContext();

    if (!applicationId) {
        // TODO: Should we log an error here or throw an error?
        debug.error('Application ID is required');
    }

    const { runRecipes } = useRunCustomerRecipes();

    const runRecipeParams = useMemo(() => {
        const params = {
            action,
            applicationId,
            platformTenantId: clientId
        } as RunRecipeParams;

        if (isApiSearchEnabled && searchField) {
            params.recipeParams = {
                [searchField]: search
            };
        }

        return params;
    }, [
        action,
        clientId,
        applicationId,
        search,
        isApiSearchEnabled,
        searchField
    ]) as RunRecipeParams;

    useEffect(() => {
        const fetchOptions = async () => {
            setLoading(true);
            try {
                const results = await runRecipes(runRecipeParams);
                // @ts-expect-error fix type - I don't think we'll be able to fix this...
                const entities = results?.[0]?.data?.entities;

                if (entities?.length) {
                    const selectOptions = entities.map((entity: any) => ({
                        label: entity[label || valueAccessor],
                        value: entity[valueAccessor]
                    })) as SelectOption[];

                    setOptions((prevOptions: SelectOption[]) =>
                        _.uniqBy([...prevOptions, ...selectOptions], 'value')
                    );
                }
            } catch (e) {
                // TODO: handle error
                debug.error(e);
            } finally {
                setLoading(false);
            }
        };

        fetchOptions();
    }, [label, valueAccessor, runRecipes, runRecipeParams]);

    return (
        <IcebergFormSelect
            {...props}
            onInputChange={(value) => {
                const trimmedValue = (value || '').trim();
                if (
                    isApiSearchEnabled &&
                    !!trimmedValue &&
                    trimmedValue !== search
                ) {
                    debouncedSetSearch(trimmedValue);
                }
            }}
            components={{
                LoadingIndicator
            }}
            loading={loading}
            showLoadingState={loading}
            options={options}
        />
    );
};

const FormSelectWithDataLoader = (props: FormSelectHandlerProps) => {
    const { operation } = props;
    const { serviceType } = operation!;

    if (serviceType === 'workato') {
        return <FormSelectWorkatoDataLoader {...props} />;
    }

    // TODO: Implement the asm call. https://auviknetworks.atlassian.net/browse/SAAS-2214

    return <IcebergFormSelect {...props} />;
};

export const FormSelectHandler = (props: FormSelectHandlerProps) => {
    // TODO: Should we check for more than just operation?
    if (props.operation) {
        return <FormSelectWithDataLoader {...props} />;
    }

    return <IcebergFormSelect {...props} />;
};
